import { ReactNode } from 'react'

interface CardProps {
    children: ReactNode
    variant?: 'default' | 'elevated' | 'feature'
    className?: string
}

export default function Card({ children, variant = 'default', className = '' }: CardProps) {
    const variantStyles = {
        default: 'glass shadow-glass',
        elevated: 'glass-elevated',
        feature: 'glass-prominent',
    }

    return (
        <div className={`rounded-2xl p-6 ${variantStyles[variant]} ${className}`}>
            {children}
        </div>
    )
}

interface FeatureCardProps {
    icon: ReactNode
    title: string
    description: string
    className?: string
}

export function FeatureCard({ icon, title, description, className = '' }: FeatureCardProps) {
    return (
        <Card variant="default" className={`flex flex-col items-center gap-6 p-10 text-center ${className}`}>
            <div className="flex justify-center items-center w-16 h-16 rounded-full gradient-primary">
                <div className="w-8 h-8 text-white/85">
                    {icon}
                </div>
            </div>
            <div className="flex flex-col gap-3">
                <h3 className="text-xl font-semibold text-primary-color">{title}</h3>
                <p className="text-base font-normal text-secondary-color">{description}</p>
            </div>
        </Card>
    )
}

interface TestimonialCardProps {
    quote: string
    name: string
    role: string
    avatar?: string
    className?: string
}

export function TestimonialCard({ quote, name, role, avatar, className = '' }: TestimonialCardProps) {
    return (
        <Card variant="default" className={`flex flex-col gap-6 p-8 ${className}`}>
            <p className="text-lg font-normal italic text-secondary-color">"{quote}"</p>
            <div className="flex items-center gap-4">
                {avatar ? (
                    <img
                        src={avatar}
                        alt={name}
                        className="w-10 h-10 object-cover rounded-full border-2 border-white/40"
                    />
                ) : (
                    <div className="w-10 h-10 rounded-full gradient-primary flex items-center justify-center">
                        <span className="text-white/85 font-semibold">{name.charAt(0)}</span>
                    </div>
                )}
                <div className="flex flex-col">
                    <span className="text-base font-semibold text-primary-color">{name}</span>
                    <span className="text-sm font-normal text-secondary-color">{role}</span>
                </div>
            </div>
        </Card>
    )
}
